<script setup lang="ts">
import type { Props } from '@/shared/ui/AppInput/model/types/appInput';

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  type: 'text',
  isClearable: false,
  modelValue: '',
  mask: '',
  isDropdown: false,
  disabled: false,
  error: '',
  attr: () => ({}),
});

const emit = defineEmits<{
  (e: 'update:modelValue' | 'input', value: string | number): void;
  (e: 'clear' | 'search' | 'focus' | 'blur' | 'click'): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const isOpened: Ref<boolean> = ref(false);
const isActive: Ref<boolean> = ref(false);

const isShowClearButton: ComputedRef<boolean> = computed(
  () => props.isClearable && !!props.modelValue,
);
const isShowArrowDown: ComputedRef<boolean> = computed(() =>
  props.isDropdown !== undefined ? props.isDropdown : props.modelValue === '',
);

const handleInput = (event: Event): void => {
  const target = event.target instanceof HTMLInputElement ? event.target : null;
  if (target) {
    const value = target.value;
    emit('update:modelValue', value);
    emit('input', value);
  }
};

const handleClear = (): void => {
  emit('update:modelValue', '');
  emit('input', '');
  emit('clear');
};

const focusHandler = (): void => {
  isOpened.value = true;
  isActive.value = true;
  emit('focus');
};

const blurHandler = (): void => {
  isOpened.value = false;
  isActive.value = false;
  emit('blur');
};

const clickHandler = (): void => {
  emit('click');
};
</script>

<template>
  <div class="app-input">
    <input
      ref="inputRef"
      v-maska="mask"
      :value="modelValue"
      :class="[
        'app-input__field',
        { _clearable: isClearable },
        { _error: error },
        { _disabled: disabled },
        { _active: isActive },
      ]"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      v-bind="attr"
      @input="handleInput"
      @focus="focusHandler"
      @blur="blurHandler"
    />
    <button
      v-if="isShowClearButton"
      class="app-input__clear-button"
      data-testid="input-clear-button"
      type="button"
      aria-label="Очистить поле"
      @click.stop="handleClear"
    >
      <AppIcon name="Cross" />
    </button>
    <button
      v-if="isShowArrowDown"
      :class="['app-input__arrow-button', { _opened: isOpened }]"
      data-testid="input-arrow-button"
      aria-label="Открыть или закрыть список"
      @click.prevent="clickHandler"
    >
      <AppIcon name="ArrowDownSmall" />
    </button>
    <span v-if="error" class="app-input__error" data-testid="input-error">{{
      error
    }}</span>
  </div>
</template>

<style lang="less">
.app-input {
  position: relative;
  border-radius: @main-size;
  width: 100%;

  &__field {
    .input-reset();

    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: @color-elephant;
    background-color: #fff;
    width: 100%;
    height: 45px;
    border-radius: @main-size;
    border: 1px solid @color-gallery;
    padding: @size-x3 @size-x4;
    transition: border-color 0.3s ease;

    &::placeholder {
      font-weight: 500;
      color: @color-manatee;
    }

    &._clearable {
      padding: @size-x3 @size-x10 @size-x3 @size-x4;
    }

    &._error {
      background-color: #fef3ee;
      border: 1px solid @color-crimson;
    }

    &._disabled {
      pointer-events: none;
      color: @color-silver-chalice;

      &::placeholder {
        color: @color-silver-chalice;
      }
    }

    &._active:not(._error) {
      border: 1px solid @color-persimmon;
    }

    &:not(._error)::active {
      border: 1px solid @color-persimmon;
    }
  }

  &__clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    right: @size-x4;
    min-width: @size-x6;
    min-height: @size-x6;
    transform: translateY(-50%);

    .app-icon {
      width: @size-x3;
      height: @size-x3;
    }
  }

  &__arrow-button {
    display: inline-flex;
    position: absolute;
    top: 50%;
    right: @size-x5;
    transform: translateY(-50%) rotate(0);
    transition: transform @A ease-in-out;

    .app-icon {
      width: @size-x5;
      height: @size-x5;
    }

    &._opened {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  &__error {
    display: block;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-crimson;
    margin-top: @size-x3;
  }
}
</style>
